/**
 * scratchOverlay — pure math for Basil's Turn-Table Touch "DJ scratch"
 * procedural pose overlay (Q-032).
 *
 * Basil's shipped GLB (`basil_harnessed.glb`) is a merged body with no
 * limb nodes, so the scratch pose is root-level only: a crouch-over-the-
 * deck squash, a lean toward the dial with a paw-working wobble, a snappy
 * flick on every rhythm hit, and a short rattle on a miss. The math lives
 * here as pure functions so unit tests can assert decay/bounds without a
 * Babylon scene; `Basil.applyAbilityPoseOverlay` applies the result to
 * `modelRoot` (never the physics capsule).
 *
 * Zero per-frame allocations: `advanceScratchOverlay` mutates the state
 * in place and `computeScratchOverlay` writes into a caller-owned output.
 */

import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

/** Mutable per-cat overlay state (allocated once, advanced per frame). */
export interface ScratchOverlayState {
  /** Eased 0..1 activation blend toward `active` (rate dt*8). */
  blend: number;
  /** Accumulated scratch phase, advances at 10 rad/s while non-idle. */
  phase: number;
  /** Hit-flick envelope, set to 1 on TURN_TABLE_TOUCH_HIT, decays at 7/s (~140ms). */
  flick: number;
  /** Miss-jolt envelope, set to 1 on TURN_TABLE_TOUCH_MISS, decays at 5/s. */
  jolt: number;
}

/** Composable pose deltas — applied with += (offsets) and *= (scales). */
export interface ScratchOverlayPose {
  /** root.position.x += dx */
  dx: number;
  /** root.rotation.z += dRotZ */
  dRotZ: number;
  /** root.scaling.x *= scaleXMul */
  scaleXMul: number;
  /** root.scaling.y *= scaleYMul */
  scaleYMul: number;
}

export function createScratchOverlayState(): ScratchOverlayState {
  return { blend: 0, phase: 0, flick: 0, jolt: 0 };
}

export function createScratchOverlayPose(): ScratchOverlayPose {
  return { dx: 0, dRotZ: 0, scaleXMul: 1, scaleYMul: 1 };
}

/**
 * Subscribe the overlay state to the TTT rhythm events: a successful hit
 * arms the flick envelope, a miss arms the jolt. Returns a single
 * unsubscribe-all — Basil pushes it into its dispose teardown (mirroring
 * the HARNESS_STATE_CHANGED listener pattern).
 */
export function wireScratchOverlayEvents(state: ScratchOverlayState): () => void {
  const unsubs = [
    gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
      state.flick = 1;
    }),
    gameEvents.on(GameEvents.TURN_TABLE_TOUCH_MISS, () => {
      state.jolt = 1;
    }),
  ];
  return () => {
    for (const unsub of unsubs) {
      unsub();
    }
  };
}

/**
 * Advance the overlay envelopes by `dt` seconds.
 *
 * Returns `false` when the overlay is fully idle (blend settled near 0,
 * no flick/jolt in flight) — the caller should early-out and apply
 * nothing, keeping the per-frame cost ~zero while TTT is inactive.
 */
export function advanceScratchOverlay(
  state: ScratchOverlayState,
  dt: number,
  active: boolean,
): boolean {
  const target = active ? 1 : 0;
  state.blend += (target - state.blend) * Math.min(1, dt * 8);
  if (state.blend < 0.01 && state.flick <= 0 && state.jolt <= 0) {
    // Fully idle — snap the residual blend so it can't linger sub-epsilon.
    state.blend = 0;
    return false;
  }
  state.phase += dt * 10;
  state.flick = Math.max(0, state.flick - dt * 7);
  state.jolt = Math.max(0, state.jolt - dt * 5);
  return true;
}

/**
 * Compute the pose deltas for the current overlay state.
 *
 * @param facing +1 east / -1 west (mirrors the flick/lean with the model)
 * @param calm   1 - smoothedMoveAmount — walking dampens the scratch pose
 * @param out    caller-owned output (no per-frame allocation)
 */
export function computeScratchOverlay(
  state: ScratchOverlayState,
  facing: number,
  calm: number,
  out: ScratchOverlayPose,
): ScratchOverlayPose {
  const b = state.blend * calm;
  // Crouch over the deck: shorter + slightly wider.
  out.scaleYMul = 1 - 0.07 * b;
  out.scaleXMul = 1 + 0.05 * b;
  // Lean toward the dial + paw-working wobble (~1.6 Hz at phase 10 rad/s),
  // plus a snappy jab toward the dial on each hit (decays ~140ms, same
  // envelope family as launchCompress).
  out.dRotZ =
    -facing * 0.10 * b +
    Math.sin(state.phase) * 0.05 * b +
    -facing * 0.12 * state.flick;
  // Hit-flick shove toward the dial; miss-rattle mirrors deniedInputRattle.
  out.dx =
    facing * 0.07 * state.flick +
    Math.sin(state.phase * 55) * 0.03 * state.jolt;
  return out;
}
